import {
  Box,
  Flex,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text
} from '@chakra-ui/react';
import React from 'react';

import type { DictionaryOption } from '../../interface/api';
import type { CaseSpecialtyData } from '../../interface/caseSpecialty';
import { useCodingScreen } from '../../interface/context';
import type { SlotProps } from '../../interface/slotProps';
import {
  CaseSpecialtyEditForm,
  type CaseSpecialtyEditStatus
} from './CaseSpecialtyEditForm';

const na = (v: unknown): string =>
  typeof v === 'string' || typeof v === 'number' ? String(v) : 'N/A';

const INTER_STACK = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const HOVER_HINT_SX = {
  cursor: 'help',
  textDecoration: 'underline dotted var(--border-strong, #c9c8ce)',
  textUnderlineOffset: '3px',
  '&:hover, &:focus-visible': {
    textDecorationColor: 'var(--text-muted, #7a7682)'
  }
} as const;

interface CaseSpecialtyRow {
  readonly id: string;
  readonly label: string;
  readonly value?: string;
}

const CaseSpecialtyRowView: React.FC<{ readonly row: CaseSpecialtyRow }> = ({
  row
}) => (
  <Box display="contents" data-testid={`case-specialty-${row.id}`}>
    <Text
      as="span"
      fontSize="11px"
      fontWeight={400}
      color="#4a4751"
      lineHeight="1.6"
      whiteSpace="nowrap"
    >
      {row.label}
    </Text>
    <Text
      as="span"
      fontSize="11.5px"
      fontWeight={600}
      color="#1a1818"
      lineHeight="1.6"
      whiteSpace="nowrap"
    >
      {na(row.value)}
    </Text>
  </Box>
);

const ROLE_PRIMARY = {
  bg: 'var(--ai-faint, #efeafc)',
  color: 'var(--ai, #6b4bd4)'
} as const;

const ROLE_NEUTRAL = {
  bg: 'var(--panel-2, #eceef2)',
  color: 'var(--text-2, #5b5966)'
} as const;

const ServiceRoleBadge: React.FC<{ readonly role: string }> = ({ role }) => {
  const palette =
    role.toLowerCase() === 'primary' ? ROLE_PRIMARY : ROLE_NEUTRAL;
  return (
    <Box
      as="span"
      data-testid="case-specialty-role"
      fontFamily={INTER_STACK}
      fontSize="9px"
      fontWeight={700}
      textTransform="uppercase"
      letterSpacing="0.07em"
      lineHeight="1.2"
      borderRadius="3px"
      px="5px"
      py="2px"
      flexShrink={0}
      bg={palette.bg}
      color={palette.color}
    >
      {role.toUpperCase()}
    </Box>
  );
};

/** The Case specialty dropdown's dictionary -- filtered per-render to the case's own facility via
 *  `facility`/`facilityColumn` (see `interface/api.ts`'s `DictionaryRequest`). */
const SPECIALTY_DICTIONARY_BASE = {
  dictionaryName: 'Note Specialty',
  columnName: 'Note Specialty',
  facilityColumn: 'Facility'
} as const;

/** The Department dropdown's dictionary -- the abbreviation is what the coder sees/picks;
 *  `pairedColumnName` silently carries the matching "Unit/Department ID" cell for the save's
 *  `scheduling_department_code`. */
const DEPARTMENT_DICTIONARY = {
  dictionaryName: 'Department',
  columnName: 'Unit/Department Abbreviation',
  pairedColumnName: 'Unit/Department ID'
} as const;

const LOAD_FAILED = "Couldn't load the case specialty lists. Try again.";
const NO_OPTIONS = 'No options are available to choose from.';
const SAVE_FAILED = "Couldn't save the case specialty. Try again.";

export const CaseSpecialty: React.FC<SlotProps> = ({ value, mode: slotMode }) => {
  const { api } = useCodingScreen();
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLSpanElement>(null);
  const specialtySelectRef = React.useRef<HTMLSelectElement>(null);

  const data: CaseSpecialtyData =
    value != null && typeof value === 'object' && !Array.isArray(value)
      ? (value as CaseSpecialtyData)
      : {};

  const [mode, setMode] = React.useState<'view' | 'edit'>('view');
  const [isOpen, setIsOpen] = React.useState(false);
  // The view panel's rendered width, pinned for the edit session so the form doesn't resize the
  // popover (a `max-content` select would otherwise grow to its longest option).
  const [editWidth, setEditWidth] = React.useState<number | undefined>(
    undefined
  );
  const [status, setStatus] = React.useState<CaseSpecialtyEditStatus>('loading');
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>(
    undefined
  );
  const [specialtyOptions, setSpecialtyOptions] = React.useState<
    readonly DictionaryOption[]
  >([]);
  const [departmentOptions, setDepartmentOptions] = React.useState<
    readonly DictionaryOption[]
  >([]);

  const canEdit = data.permission === 'allowed' && slotMode === 'coding';
  const canSave = Boolean(
    canEdit && api?.updateCaseSpecialty && data.caseRunUid && data.serviceLine
  );

  const enterEdit = React.useCallback(() => {
    if (!canEdit) return;
    setEditWidth(popoverRef.current?.offsetWidth || undefined);
    setMode('edit');
    setIsOpen(true);
  }, [canEdit]);

  const editSession = React.useRef(0);
  const committedSpecialty = React.useRef<string | undefined>(undefined);
  const committedDepartment = React.useRef<string | undefined>(undefined);

  const resetEditSession = React.useCallback(() => {
    editSession.current += 1;
    setMode('view');
    setStatus('loading');
    setErrorMessage(undefined);
    setSpecialtyOptions([]);
    setDepartmentOptions([]);
  }, []);

  const leaveEdit = React.useCallback(() => {
    resetEditSession();
    triggerRef.current?.focus();
  }, [resetEditSession]);

  // Same focus-ownership reasoning as BillingLocation/PatientTypeInfo: Chakra's own focus hooks are
  // inert here (the panel is already mounted), so entry is driven from this component, unconditionally.
  React.useEffect(() => {
    if (mode !== 'edit') return undefined;
    const id = requestAnimationFrame(() => {
      if (specialtySelectRef.current && !specialtySelectRef.current.disabled) {
        specialtySelectRef.current.focus();
      } else {
        popoverRef.current?.focus();
      }
    });
    return () => cancelAnimationFrame(id);
  }, [mode]);

  React.useEffect(() => {
    if (mode !== 'edit' || status !== 'ready') return;
    const active = document.activeElement;
    if (active !== document.body && active !== popoverRef.current) return;
    specialtySelectRef.current?.focus();
  }, [mode, status, data.specialty, data.department]);

  const handleClose = React.useCallback(() => {
    if (mode === 'edit') return;
    setIsOpen(false);
  }, [mode]);

  React.useEffect(() => {
    if (!isOpen || status === 'saving') return undefined;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setIsOpen(false);
      resetEditSession();
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isOpen, status, resetEditSession]);

  React.useEffect(() => {
    if (mode !== 'edit') return undefined;

    const load = api?.loadDictionaryOptions;
    if (!load) {
      setSpecialtyOptions([]);
      setDepartmentOptions([]);
      setStatus('unavailable');
      setErrorMessage(LOAD_FAILED);
      return undefined;
    }

    let abandoned = false;
    setStatus('loading');
    setErrorMessage(undefined);
    Promise.all([
      load({ ...SPECIALTY_DICTIONARY_BASE, facility: data.facility }),
      load(DEPARTMENT_DICTIONARY)
    ])
      .then(([specialties, departments]) => {
        if (abandoned) return;
        setSpecialtyOptions(specialties);
        setDepartmentOptions(departments);
        const ready = specialties.length > 0 && departments.length > 0;
        setStatus(ready ? 'ready' : 'unavailable');
        setErrorMessage(ready ? undefined : NO_OPTIONS);
      })
      .catch(() => {
        if (abandoned) return;
        setSpecialtyOptions([]);
        setDepartmentOptions([]);
        setStatus('unavailable');
        setErrorMessage(LOAD_FAILED);
      });

    return () => {
      abandoned = true;
    };
  }, [mode, api, data.facility]);

  React.useEffect(() => {
    if (mode === 'edit' && !canEdit) leaveEdit();
  }, [mode, canEdit, leaveEdit]);

  const caseRunUidRef = React.useRef(data.caseRunUid);
  React.useEffect(() => {
    if (caseRunUidRef.current === data.caseRunUid) return;
    caseRunUidRef.current = data.caseRunUid;
    committedSpecialty.current = undefined;
    committedDepartment.current = undefined;
    if (mode === 'edit') leaveEdit();
  }, [data.caseRunUid, mode, leaveEdit]);

  const editedSpecialty = React.useRef(data.specialty);
  React.useEffect(() => {
    if (editedSpecialty.current === data.specialty) return;
    // Defer while a save is in flight -- advancing the ref here would swallow the change instead of
    // acting on it once the write settles.
    if (status === 'saving') return;
    const wasOurs = committedSpecialty.current === data.specialty;
    editedSpecialty.current = data.specialty;
    committedSpecialty.current = undefined;
    if (mode === 'edit' && !wasOurs) leaveEdit();
  }, [data.specialty, mode, status, leaveEdit]);

  const editedDepartment = React.useRef(data.department);
  React.useEffect(() => {
    if (editedDepartment.current === data.department) return;
    if (status === 'saving') return;
    const wasOurs = committedDepartment.current === data.department;
    editedDepartment.current = data.department;
    committedDepartment.current = undefined;
    if (mode === 'edit' && !wasOurs) leaveEdit();
  }, [data.department, mode, status, leaveEdit]);

  const onSave = async (
    specialty: string,
    department: string,
    departmentCode: string | undefined
  ) => {
    const update = api?.updateCaseSpecialty;
    if (!canSave || !update || !data.caseRunUid || !data.serviceLine) {
      return;
    }
    const session = editSession.current;
    setStatus('saving');
    setErrorMessage(undefined);
    try {
      await update({
        caseRunUid: data.caseRunUid,
        serviceLine: data.serviceLine,
        specialty,
        department,
        departmentCode
      });
    } catch {
      // The coder can abandon, or the case change, while the PUT is outstanding; this settlement
      // then belongs to a session that no longer exists.
      if (editSession.current !== session) return;
      setStatus('ready');
      setErrorMessage(SAVE_FAILED);
      return;
    }
    if (editSession.current !== session) return;
    committedSpecialty.current = specialty;
    committedDepartment.current = department;
    leaveEdit();
  };

  if (slotMode === 'grading') {
    return (
      <Text
        as="span"
        data-testid="case-specialty"
        aria-label={`Service line: ${na(data.serviceLine)}`}
        fontFamily={INTER_STACK}
        fontSize="12px"
        fontWeight={500}
        color="#1a1818"
      >
        {na(data.serviceLine)}
      </Text>
    );
  }

  const rows: readonly CaseSpecialtyRow[] = [
    { id: 'specialty', label: 'Case specialty', value: data.specialty },
    { id: 'department', label: 'Department', value: data.department },
    { id: 'billing-area', label: 'Billing area', value: data.billingArea }
  ];

  const serviceRole =
    typeof data.serviceRole === 'string' && data.serviceRole.trim() !== ''
      ? data.serviceRole
      : undefined;

  return (
    <Flex as="span" align="center" gap="6px">
      <Popover
        isOpen={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={handleClose}
        trigger={mode === 'edit' ? 'click' : 'hover'}
        closeOnBlur={mode !== 'edit'}
        closeOnEsc={mode !== 'edit'}
        placement="bottom-start"
      >
        <PopoverTrigger>
          <Text
            as="span"
            ref={triggerRef}
            tabIndex={0}
            onClick={mode === 'edit' ? undefined : enterEdit}
            onKeyDown={(event) => {
              if (!canEdit) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                enterEdit();
              }
            }}
            aria-label={`Case specialty: ${na(data.specialty)}`}
            data-testid="case-specialty"
            fontFamily={INTER_STACK}
            fontSize="12px"
            fontWeight={500}
            color="#1a1818"
            sx={HOVER_HINT_SX}
          >
            {na(data.specialty)}
          </Text>
        </PopoverTrigger>
        <Portal>
          <PopoverContent
            ref={popoverRef}
            onKeyDown={(event) => {
              if (mode !== 'edit') return;
              if (event.key === 'Escape') {
                event.stopPropagation();
                leaveEdit();
              }
            }}
            data-testid="case-specialty-popover"
            data-edit-permission={data.permission ?? 'pending'}
            bg="#f9f8fa"
            border="1px solid #ececef"
            borderRadius="9px"
            boxShadow="0 14px 36px -14px rgba(0,0,0,0.4), 0 2px 8px -4px rgba(0,0,0,0.18)"
            padding="11px 13px"
            width={
              mode === 'edit' && editWidth ? `${editWidth}px` : 'max-content'
            }
            minWidth="226px"
            maxWidth="360px"
            fontFamily={INTER_STACK}
          >
            {mode === 'edit' ? (
              <CaseSpecialtyEditForm
                key={`${data.specialty ?? ''}::${data.department ?? ''}::${data.departmentCode ?? ''}`}
                currentSpecialty={
                  typeof data.specialty === 'string' ? data.specialty : ''
                }
                currentDepartment={
                  typeof data.department === 'string' ? data.department : ''
                }
                currentDepartmentCode={
                  typeof data.departmentCode === 'string'
                    ? data.departmentCode
                    : ''
                }
                billingArea={na(data.billingArea)}
                specialtyOptions={specialtyOptions}
                departmentOptions={departmentOptions}
                status={status}
                errorMessage={errorMessage}
                canSave={canSave}
                specialtySelectRef={specialtySelectRef}
                onSave={onSave}
                onCancel={leaveEdit}
              />
            ) : (
              <Box
                display="grid"
                gridTemplateColumns="2fr 2fr 1fr"
                columnGap="14px"
                rowGap="2px"
                alignItems="start"
              >
                {rows.map((row) => (
                  <CaseSpecialtyRowView key={row.id} row={row} />
                ))}
                <Box
                  as="button"
                  type="button"
                  data-testid="case-specialty-edit"
                  aria-label="Edit case specialty"
                  title="Edit case specialty"
                  onClick={enterEdit}
                  // The button's grid cell (col 3, spanning all rows) must stay reserved even when
                  // it's not editable -- without it, the label/value pairs auto-place 3-per-row
                  // instead of 2-per-row and scramble across the wrong rows/columns.
                  visibility={canEdit ? 'visible' : 'hidden'}
                  aria-hidden={!canEdit}
                  tabIndex={canEdit ? 0 : -1}
                  gridColumn="3"
                  gridRow={`1 / span ${rows.length}`}
                  justifySelf="end"
                  alignSelf="start"
                  background="none"
                  border="1px solid var(--border-strong, #c9c8ce)"
                  borderRadius="5px"
                  px="6px"
                  py="2px"
                  fontSize="13px"
                  lineHeight="1.6"
                  color="var(--text-muted, #7a7682)"
                  cursor="pointer"
                  _hover={{
                    borderColor: 'var(--primary, #6b4bd4)',
                    color: 'var(--primary, #6b4bd4)',
                    background: 'var(--ai-faint, #efeafc)'
                  }}
                >
                  ✏
                </Box>
              </Box>
            )}
          </PopoverContent>
        </Portal>
        {serviceRole && <ServiceRoleBadge role={serviceRole} />}
      </Popover>
    </Flex>
  );
};
